// apps/web/src/features/kronos/timer/ActiveTimerCard.tsx
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import EditIcon from '@mui/icons-material/EditOutlined';
import SensorsIcon from '@mui/icons-material/Sensors';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../core/theme/theme';

interface ActiveTimerCardProps {
  status: 'running' | 'paused';
  projectLabel: string;
  designationLabel: string;
  description: string;
  elapsedMs: number;
  autoStopLabel: string | null;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onEdit: (e: React.MouseEvent<HTMLElement>) => void;
  onDescriptionChange: (description: string) => void;
}

function formatCounter(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export default function ActiveTimerCard({
  status,
  projectLabel,
  designationLabel,
  description,
  elapsedMs,
  autoStopLabel,
  onPause,
  onResume,
  onStop,
  onEdit,
  onDescriptionChange,
}: ActiveTimerCardProps) {
  const { t } = useTranslation();
  const isRunning = status === 'running';

  return (
    <Box
      sx={{
        bgcolor: 'rgba(237, 200, 163, 0.6)',
        p: 3,
        mb: 3,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
      }}
      data-testid="active-timer-card"
    >
      {/* Brand watermark — large, rotated, cropped by the card */}
      <Box
        component="img"
        src="/images/brand-watermark.png"
        alt=""
        sx={{
          position: 'absolute',
          bottom: -60,
          right: -40,
          width: 340,
          height: 340,
          opacity: 0.08,
          pointerEvents: 'none',
          transform: 'rotate(-15deg)',
        }}
      />

      {/* Left: status + project info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <SensorsIcon
            sx={{
              fontSize: 18,
              ...(isRunning && {
                animation: 'timerPulse 1.5s ease-in-out infinite',
              }),
              '@keyframes timerPulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.3 },
              },
            }}
          />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {isRunning ? t('kronos.timer.activeTimer') : t('kronos.timer.paused')}
          </Typography>
          {autoStopLabel && (
            <Typography variant="caption" sx={{ ml: 1 }}>
              {autoStopLabel}
            </Typography>
          )}
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700 }} noWrap>
          {projectLabel}
        </Typography>
        <Typography variant="body2" noWrap>
          {designationLabel}
        </Typography>
        <TextField
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={t('kronos.tracker.descriptionPlaceholder')}
          variant="standard"
          size="small"
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          slotProps={{
            input: {
              disableUnderline: true,
              sx: {
                fontSize: 13,
                py: 1,
                px: 1.5,
                mt: 1,
                bgcolor: 'rgba(0,0,0,0.06)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.09)' },
              },
            },
          }}
        />
      </Box>

      {/* Right: counter + buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', sm: 'flex-end' }, gap: 1 }}>
        <Typography
          sx={{
            fontSize: { xs: 40, sm: 48 },
            fontWeight: 900,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
            fontFamily: 'monospace, Gabarito',
          }}
        >
          {formatCounter(elapsedMs)}
        </Typography>
        <Stack direction="row" spacing={1}>
          {isRunning ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<PauseIcon />}
              onClick={onPause}
              sx={{
                bgcolor: colors.darkBg,
                color: colors.white,
                '&:hover': { bgcolor: '#2a2d33' },
              }}
            >
              {t('kronos.timer.pauseTimer')}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={onResume}
              sx={{
                bgcolor: colors.darkBg,
                color: colors.white,
                '&:hover': { bgcolor: '#2a2d33' },
              }}
            >
              {t('kronos.timer.resumeTimer')}
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<StopIcon />}
            onClick={onStop}
            sx={{
              bgcolor: colors.darkBg,
              color: colors.white,
              '&:hover': { bgcolor: '#2a2d33' },
            }}
          >
            {t('kronos.timer.stopTimer')}
          </Button>
          <IconButton
            size="small"
            onClick={(e) => onEdit(e)}
            sx={{ bgcolor: colors.white, '&:hover': { bgcolor: colors.peach } }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
}
